import React, { Fragment, useEffect, useMemo } from 'react'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'

import { StyledCheckoutButton } from '@src/components/CheckoutModal/CheckoutButton.styles'
import { CheckoutButtonWrapperMobile } from '@src/components/CheckoutModal/CheckoutButtonWrapperMobile'
import { LoadingSpinner } from '@src/components/Loaders/LoadingSpinner'
import { AddOnItemCard } from './AddOnItemCard'
import { Container, Description } from './AddOnItems.styles'

import {
  OutletFulfilmentStateType,
  useOutletFulfilment,
} from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/useOutletFulfilment'
import {
  CheckoutRoute,
  useCheckoutRouter,
} from '@src/hooks/useCheckoutRouter/useCheckoutRouter'
import {
  useBasketTotals,
  UseBasketTotalsResponseData,
} from '@src/hooks/useTotals/useBasketTotals'
import { useBasketTotalsContextOptional } from '@src/hooks/useTotals/BasketTotalsContext'
import { AsyncDataWrapper } from '@src/hooks/types'
import { useBasketItems } from '@src/hooks/useBasketItems/useBasketItems'
import {
  NarrowFulfilmentMethodInputType,
  MenuItemGroupFulfillmentMethod,
} from '@src/graphql-types'
import { addOnMenuItemsDocument } from './queries/__generated__/addOnMenuItems.graphql-interface'

// TODO sorry i feel like this should already exist but i cant find it
export const ChosenFulfilmentToMenuItemGroupFulfillmentMethod: Record<
  NarrowFulfilmentMethodInputType,
  MenuItemGroupFulfillmentMethod
> = {
  [NarrowFulfilmentMethodInputType.DELIVERY]:
    MenuItemGroupFulfillmentMethod.DELIVERY,
  [NarrowFulfilmentMethodInputType.COLLECTION]:
    MenuItemGroupFulfillmentMethod.COLLECTION,
  [NarrowFulfilmentMethodInputType.TABLE]: MenuItemGroupFulfillmentMethod.TABLE,
}

export const AddOnItems: React.FC<{
  setManualLoadingState: (manualLoadingState: boolean) => void
  setAddOnItemsHeader: (addOnItemsHeader: string) => void
  basketTotals?: AsyncDataWrapper<UseBasketTotalsResponseData>
}> = ({
  setManualLoadingState,
  setAddOnItemsHeader,
  basketTotals: basketTotalsProp,
}) => {
  const { override } = useCheckoutRouter()
  const { t } = useTranslation('addOnItems')
  const { items } = useBasketItems()

  // Try to get basketTotals from context first, fallback to prop or hook
  const contextData = useBasketTotalsContextOptional()
  const basketTotalsHookResult = useBasketTotals()
  const basketTotals =
    basketTotalsProp ||
    (contextData ? contextData.basketTotals : null) ||
    basketTotalsHookResult

  const {
    outlet,
    data: { currentFulfilment },
  } = useOutletFulfilment({
    stateType: OutletFulfilmentStateType.GLOBAL,
  })

  const { data, loading } = useQuery(
    addOnMenuItemsDocument,
    basketTotals.data?.checkoutBasketTotals
      ? {
          variables: {
            outletId: outlet.id,
            subTotal:
              basketTotals.data.checkoutBasketTotals.nonAddOnBasketItemsTotal,
            fulfilmentMethods: [
              ChosenFulfilmentToMenuItemGroupFulfillmentMethod[
                currentFulfilment.narrowType
              ],
            ],
            endOfPrep: currentFulfilment.endOfPrep?.toISOString(),
          },
          onCompleted: data => {
            setManualLoadingState(false)
            if (!data.addOnMenuItems.length) {
              override(CheckoutRoute.PAYMENT)
            }
          },
          onError: _error => {
            override(CheckoutRoute.PAYMENT)
          },
        }
      : { skip: true }
  )

  useEffect(() => {
    if (
      basketTotals?.data &&
      outlet?.restaurant?.minimumBasketTotalForAddOns &&
      basketTotals.data.basketItemsTotal <
        outlet.restaurant.minimumBasketTotalForAddOns
    )
      override(CheckoutRoute.PAYMENT)
  }, [])

  const hasAddOnItemsInBasket = useMemo(() => {
    if (!data?.addOnMenuItems || !items.length) return false

    const addOnMenuItemIds = data.addOnMenuItems.map(item => item.menuItemId)

    return items.some(
      item => addOnMenuItemIds.includes(item.menuItemId) && item.quantity > 0
    )
  }, [data, items])

  useEffect(() => {
    const name = data?.addOnMenuItems?.[0]?.parentMenu?.name
    if (name) {
      setAddOnItemsHeader(name)
    }
  }, [data])

  if (loading || !basketTotals.data?.checkoutBasketTotals) {
    setManualLoadingState(loading)
    return <LoadingSpinner />
  }

  if (!data || !data.addOnMenuItems.length) {
    return null
  }

  const { name: restaurantName } = outlet.restaurant

  return (
    <Fragment>
      <Container windowHeight={window.innerHeight}>
        <Description>
          {data.addOnMenuItems[0]?.parentMenu.description ||
            t('offers_desc', { restaurantName })}
        </Description>
        {data.addOnMenuItems.map(addOnItem => (
          <AddOnItemCard key={addOnItem.id} addOnItem={addOnItem} />
        ))}
      </Container>
      <CheckoutButtonWrapperMobile>
        <StyledCheckoutButton
          content={hasAddOnItemsInBasket ? t('continue') : t('no_thanks')}
          onClick={() => {
            void override(CheckoutRoute.PAYMENT)
          }}
          dataTestId="checkout-mobile-modal"
        />
      </CheckoutButtonWrapperMobile>
    </Fragment>
  )
}
